import { AutoCompleteSelect } from '@components/core-ui/form-elements/AutocompleteSelect';
import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { Input } from '@components/core-ui/form-elements/Input';
import { PhoneInput } from '@components/core-ui/form-elements/PhoneInput';
import { Select } from '@components/core-ui/form-elements/Select';
import { GenericPageError } from '@components/core-ui/GenericPageError';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { normalizeEmail } from '@helpers/normalizeEmail';
import { useAPI } from '@providers/useAPI';
import { useModalContext } from '@providers/ModalProvider';
import { AdminService, CanvasService } from 'app/API';
import React, { useEffect, useState } from 'react';

import { ButtonWrapper } from './styles/ButtonWrapper';
import { InputWrapper } from './styles/InputWrapper';

type TProps = {
  courseId: string;
  onCancel: () => void;
  onSuccess: () => void;
};

export const AddCourseStudentModal = ({ onCancel, onSuccess, courseId }: TProps) => {
  const { call } = useAPI();
  const { closeModal } = useModalContext();
  const [closeAfterSubmit, setCloseAfterSubmit] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [secondName, setSecondName] = useState('');
  const [phone, setPhone] = useState('');
  const [organization, setOrganization] = useState<string | undefined>();
  const [team, setTeam] = useState<string | undefined>();
  const [license, setLicense] = useState('RSP60');
  const [loading, setLoading] = useState(false);
  const [organizations, setOrganizations] = useState<{ label: string; value: string }[]>([]);
  const [licenses, setLicenses] = useState<any>([]);
  const [orgTeams, setOrgTeams] = useState<any>([]);

  const isValid = email && firstName && secondName && organization && team && license;

  useEffect(() => {
    call(AdminService.getAllOrganizationsList()).then((res) => {
      setOrganizations(res.map((o) => ({ label: `${o.name} (${o.slug})`, value: o.id })));
    });
    setLicenses([
      { label: 'No license', value: 'NO_LICENSE' },
      { label: 'RS Pro: 30 days', value: 'RSP30' },
      { label: 'RS Pro: 60 days', value: 'RSP60' },
    ]);
  }, []);

  useEffect(() => {
    setTeam(undefined);
    setOrgTeams([]);
    if (!organization) {
      return;
    }
    call(AdminService.getTeams({ oid: organization })).then((res) => {
      setOrgTeams(res.map((o) => ({ label: o.name, value: o.id })));
    });
  }, [organization]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');
    if (!isValid) {
      return;
    }
    setLoading(true);
    let requestErrorMessage: string | undefined;
    try {
      await call(
        () =>
          CanvasService.createCourseStudent({
            courseId,
            requestBody: {
              email: normalizeEmail(email),
              name: firstName,
              lastName: secondName,
              phone,
              organization,
              team,
              license,
            },
          }),
        {
          onError: (message) => {
            requestErrorMessage = message;
          },
        },
      );
      onSuccess();
      if (closeAfterSubmit) {
        closeModal();
      }
    } catch {
      setErrorMessage(requestErrorMessage || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper width="330px">
      {loading && <AbsoluteSpinner overlay={true} />}
      <ModalTitle>Add new student</ModalTitle>
      <GenericPageError message={errorMessage} />
      <form onSubmit={submit}>
        <InputWrapper>
          <Input
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setFirstName(event.target.value)}
            name="firstName"
            label="First name"
            type="text"
            value={firstName}
            required={true}
          />
        </InputWrapper>
        <InputWrapper>
          <Input
            name="lastName"
            label="Last name"
            type="text"
            value={secondName}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setSecondName(event.target.value)}
            required={true}
          />
        </InputWrapper>
        <InputWrapper>
          <Input
            name="email"
            label="Email"
            type="email"
            value={email}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
            required={true}
          />
        </InputWrapper>
        <InputWrapper>
          <PhoneInput
            onChange={(value) => setPhone(value)}
            defaultCountry="US"
            name="phone"
            placeholder="Phone number (e.g. +12223334455)"
            value={phone || ''}
            required={false}
          />
        </InputWrapper>
        <InputWrapper>
          <AutoCompleteSelect
            name="organization"
            label="Organization"
            value={organization}
            onChange={(_name, value) => setOrganization(value)}
            options={organizations}
          />
        </InputWrapper>
        <InputWrapper>
          <Select
            name="team"
            label="Team"
            value={team}
            disabled={!organization}
            onChange={(_name, value) => setTeam(value)}
            options={orgTeams}
          />
        </InputWrapper>
        <InputWrapper>
          <Select
            name="license"
            label="License"
            value={license}
            onChange={(_name, value) => setLicense(value)}
            options={licenses}
          />
        </InputWrapper>
        <InputWrapper className="checkbox-wrapper">
          <CheckBox
            name="close-after-submit"
            checked={closeAfterSubmit}
            onChange={(value) => setCloseAfterSubmit(value)}
          />
          <label>Close this modal after student is added</label>
        </InputWrapper>

        <ButtonWrapper>
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button type="submit" disabled={!isValid}>
            Add Student
          </Button>
        </ButtonWrapper>
      </form>
    </ModalWrapper>
  );
};
